import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import PlayoutService from '../../../services/PlayoutService';
import { PageHeader, Card, Button, Badge, EmptyState, Toggle, Field, Select } from '../../ui';

const ASSET_COLOR = '#f26a1b';   // brand — a single program
const PLAYLIST_COLOR = '#3b82f6'; // a rotation/loop

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Pull {h,m,s} out of either "HH:MM[:SS]" or "...THH:MM[:SS]".
const toHM = (val) => {
  if (!val) return null;
  const str = String(val);
  const timePart = str.includes('T') ? str.split('T')[1] : str;
  const [h = '0', m = '0', s = '0'] = timePart.split(':');
  return { h: parseInt(h, 10) || 0, m: parseInt(m, 10) || 0, s: parseInt(s, 10) || 0 };
};

// Vod.getAllPlaylists() already parses `items` into a real array
// server-side — a defensive fallback in case any call path ever sends the
// raw JSON string instead (JSON.parse on an already-parsed array throws:
// it gets coerced to a string like "1,2" first, which isn't valid JSON).
const itemsCount = (items) => {
  if (Array.isArray(items)) return items.length;
  if (typeof items === 'string') { try { return JSON.parse(items).length; } catch { return 0; } }
  return 0;
};

const pad = (n) => String(n).padStart(2, '0');
// Server-local naive string — never toISOString() here. vod_schedules dates
// carry no timezone anywhere in this system (vodFailover's server-local
// time-of-day checks, the backend's own addDurationToTime) — a UTC string
// would silently shift every block by the browser/server's offset.
const toLocalNaive = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

// Expand every schedule row into the concrete calendar events it occupies
// within [rangeStart, rangeEnd) — a recurring (dateless) row gets one event
// per matching weekday in range; a dated one-off gets exactly one, on its
// exact date.
function expandToEvents(schedules, rangeStart, rangeEnd) {
  const events = [];
  for (const s of schedules) {
    if (s.is_active === 0) continue;
    const title = s.asset_id ? (s.asset_title || 'Program') : (s.playlist_name || 'Rotation');
    const color = s.asset_id ? ASSET_COLOR : PLAYLIST_COLOR;
    const isDated = String(s.start_time || '').includes('T');

    if (isDated) {
      const start = new Date(s.start_time);
      const end = s.end_time ? new Date(s.end_time) : start;
      if (end > rangeStart && start < rangeEnd) {
        events.push({
          id: `${s.id}`, title, start, end,
          backgroundColor: color, borderColor: color,
          extendedProps: { schedule: s },
        });
      }
      continue;
    }

    const days = Array.isArray(s.days_of_week) ? s.days_of_week : [0, 1, 2, 3, 4, 5, 6];
    const sHM = toHM(s.start_time) || { h: 0, m: 0, s: 0 };
    const eHM = toHM(s.end_time);

    const day = new Date(rangeStart);
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() - 1); // one day early, for overnight spill-in
    const last = new Date(rangeEnd);
    while (day <= last) {
      if (days.includes(day.getDay())) {
        const start = new Date(day);
        start.setHours(sHM.h, sHM.m, sHM.s, 0);
        const end = new Date(day);
        if (eHM) end.setHours(eHM.h, eHM.m, eHM.s, 0); else end.setHours(23, 59, 59, 0);
        if (eHM && (eHM.h < sHM.h || (eHM.h === sHM.h && eHM.m < sHM.m))) end.setDate(end.getDate() + 1);
        if (end > rangeStart && start < rangeEnd) {
          events.push({
            id: `${s.id}-${day.toISOString().slice(0, 10)}`, title,
            start: new Date(Math.max(start, rangeStart)), end: new Date(Math.min(end, rangeEnd)),
            backgroundColor: color, borderColor: color,
            extendedProps: { schedule: s },
          });
        }
      }
      day.setDate(day.getDate() + 1);
    }
  }
  return events;
}

// Lightweight modal shell — the ui kit has no Modal export, this matches
// Card's visual language (dark, rounded) rather than pulling in react-bootstrap.
const Overlay = ({ onClose, children, width = 'tw-max-w-md' }) => (
  <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/60 tw-p-4" onClick={onClose}>
    <div className={`tw-w-full ${width}`} onClick={(e) => e.stopPropagation()}>
      <Card>{children}</Card>
    </div>
  </div>
);

const Playout = () => {
  const nav = useNavigate();
  const calendarRef = useRef(null);
  const drawerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(null);

  const [streams, setStreams] = useState([]);
  const [streamId, setStreamId] = useState(null);
  const [assets, setAssets] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [autoFailover, setAutoFailover] = useState(false);
  const [range, setRange] = useState(null); // { start, end } of the visible calendar view

  const [createForm, setCreateForm] = useState(null); // { start, end } while open
  const [panelEvent, setPanelEvent] = useState(null); // schedule row while the side panel is open
  const [fillGapsOpen, setFillGapsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ok = await PlayoutService.access();
      setAllowed(ok);
      if (ok) {
        const [streamList, assetList, playlistList, fo] = await Promise.all([
          PlayoutService.getStreams(), PlayoutService.getAssets(), PlayoutService.getPlaylists(),
          PlayoutService.getFailoverSettings().catch(() => ({ enabled: false })),
        ]);
        setStreams(streamList);
        setAssets(assetList);
        setPlaylists(playlistList);
        setAutoFailover(!!fo.enabled);
        if (streamList.length) setStreamId(streamList[0].id);
      }
    } catch { setAllowed(false); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadSchedules = useCallback(async () => {
    if (!streamId) return;
    setSchedules(await PlayoutService.getCalendar(streamId));
  }, [streamId]);
  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  // Drag an asset/playlist card from the drawer onto the calendar.
  useEffect(() => {
    if (!drawerRef.current) return;
    const draggable = new Draggable(drawerRef.current, {
      itemSelector: '.playout-draggable',
      eventData: (el) => ({
        title: el.dataset.title,
        duration: el.dataset.duration ? { seconds: Number(el.dataset.duration) } : { hours: 1 },
        extendedProps: { assetId: el.dataset.assetId || null, playlistId: el.dataset.playlistId || null },
      }),
    });
    return () => draggable.destroy();
  }, [assets, playlists]);

  const events = range ? expandToEvents(schedules, range.start, range.end) : [];

  const toggleAutoFailover = async (next) => {
    setAutoFailover(next);
    try { await PlayoutService.setFailoverSettings(next); }
    catch { setAutoFailover(!next); }
  };

  const handleDatesSet = (arg) => setRange({ start: arg.view.activeStart, end: arg.view.activeEnd });

  const handleSelect = (info) => {
    setCreateForm({ start: info.start, end: info.end });
    calendarRef.current?.getApi().unselect();
  };

  const handleEventClick = (info) => setPanelEvent(info.event.extendedProps.schedule);

  // A drag-move or resize always pins that occurrence to its new slot as a
  // one-off (start_time becomes dated) — editing "the whole recurring
  // series" from the calendar is a bigger interaction (which occurrence?
  // all future ones?) left out of this first version.
  const commitMove = async (schedule, start, end, revert) => {
    try {
      await PlayoutService.updateSchedule(schedule.id, {
        start_time: toLocalNaive(start),
        end_time: toLocalNaive(end),
        days_of_week: [0, 1, 2, 3, 4, 5, 6],
      });
      await loadSchedules();
    } catch (e) {
      revert();
      window.alert(e.response?.data?.error || 'Failed to move this program');
    }
  };
  const handleEventDrop = (info) => commitMove(info.event.extendedProps.schedule, info.event.start, info.event.end, info.revert);
  const handleEventResize = (info) => commitMove(info.event.extendedProps.schedule, info.event.start, info.event.end, info.revert);

  // External drag from the asset/playlist drawer landing on the calendar.
  const handleExternalDrop = async (info) => {
    const { assetId, playlistId } = info.draggedEl.dataset.assetId
      ? { assetId: info.draggedEl.dataset.assetId, playlistId: null }
      : { assetId: null, playlistId: info.draggedEl.dataset.playlistId };
    try {
      await PlayoutService.createSchedule({
        stream_id: streamId,
        asset_id: assetId || undefined,
        playlist_id: playlistId || undefined,
        start_time: toLocalNaive(info.date),
        end_time: playlistId ? toLocalNaive(new Date(info.date.getTime() + 3600000)) : undefined,
        days_of_week: [0, 1, 2, 3, 4, 5, 6],
        is_active: true, failover_enabled: true,
      });
      await loadSchedules();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Failed to place that program');
    }
  };

  const closeSidePanel = () => setPanelEvent(null);
  const deleteEvent = async () => {
    if (!panelEvent) return;
    if (!window.confirm('Remove this program from the schedule?')) return;
    setBusy(true);
    try { await PlayoutService.deleteSchedule(panelEvent.id); await loadSchedules(); closeSidePanel(); }
    finally { setBusy(false); }
  };
  const toggleBlockFailover = async (next) => {
    await PlayoutService.updateSchedule(panelEvent.id, { failover_enabled: next });
    setPanelEvent({ ...panelEvent, failover_enabled: next ? 1 : 0 });
    await loadSchedules();
  };
  const toggleBlockLoop = async (next) => {
    if (!panelEvent.playlist_id) return; // single-program blocks aren't a loopable concept
    await PlayoutService.updatePlaylist(panelEvent.playlist_id, { loop_enabled: next });
    await loadSchedules();
  };

  if (loading) {
    return <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>;
  }

  if (allowed === false) {
    return (
      <div className="tw-p-4 md:tw-p-6">
        <PageHeader title="Playout" subtitle="Weekly and monthly program schedule, drag-and-drop" />
        <Card>
          <EmptyState
            icon="fa-lock"
            title="Playout isn't in your plan"
            hint="Upgrade to a plan that includes the Playout calendar to schedule your program lineup visually."
            action={<Button icon="fa-tags" onClick={() => nav('/pricing')}>View plans</Button>}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="tw-p-4 md:tw-p-6">
      <PageHeader
        title="Playout"
        subtitle="Drag programs onto the calendar, fill gaps automatically, and control failover — all in one place"
        actions={
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <Select value={streamId || ''} onChange={(e) => setStreamId(Number(e.target.value))} className="tw-w-48">
              {streams.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
            <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-[10px] tw-bg-ink-800 tw-px-3 tw-py-2">
              <Toggle checked={autoFailover} onChange={toggleAutoFailover} label="Auto Failover" />
            </div>
            <Button variant="secondary" icon="fa-wand-magic-sparkles" onClick={() => setFillGapsOpen(true)} disabled={!streamId}>
              Fill Gaps
            </Button>
          </div>
        }
      />

      {!streamId ? (
        <Card><EmptyState icon="fa-video" title="No streams yet" hint="Create a stream first, then come back to schedule its programming." /></Card>
      ) : (
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-[260px_1fr]">
          <Card title="Programs" subtitle="Drag onto the calendar" bodyClass="tw-max-h-[70vh] tw-overflow-y-auto">
            <div ref={drawerRef} className="tw-space-y-2">
              {assets.map((a) => (
                <div
                  key={`a-${a.id}`} className="playout-draggable tw-cursor-grab tw-rounded-[10px] tw-border tw-border-ink-700 tw-bg-ink-800 tw-p-2.5 tw-text-sm hover:tw-border-brand"
                  data-title={a.title} data-duration={a.duration || 0} data-asset-id={a.id}
                >
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-h-2 tw-w-2 tw-rounded-full" style={{ background: ASSET_COLOR }} />
                    <span className="tw-truncate tw-font-medium tw-text-content">{a.title}</span>
                  </div>
                  <div className="tw-mt-0.5 tw-text-[0.7rem] tw-text-content-faint">
                    {a.duration ? `${Math.round(a.duration / 60)} min` : 'duration unknown'}
                  </div>
                </div>
              ))}
              {playlists.map((p) => (
                <div
                  key={`p-${p.id}`} className="playout-draggable tw-cursor-grab tw-rounded-[10px] tw-border tw-border-ink-700 tw-bg-ink-800 tw-p-2.5 tw-text-sm hover:tw-border-brand"
                  data-title={p.name} data-playlist-id={p.id}
                >
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-h-2 tw-w-2 tw-rounded-full" style={{ background: PLAYLIST_COLOR }} />
                    <span className="tw-truncate tw-font-medium tw-text-content">{p.name}</span>
                  </div>
                  <div className="tw-mt-0.5 tw-text-[0.7rem] tw-text-content-faint">Rotation · {itemsCount(p.items)} items</div>
                </div>
              ))}
              {!assets.length && !playlists.length && (
                <div className="tw-py-6 tw-text-center tw-text-xs tw-text-content-faint">
                  Upload a VOD asset or build a playlist first.
                </div>
              )}
            </div>
          </Card>

          <Card noPad bodyClass="tw-p-3">
            <div className="playout-calendar">
              <FullCalendar
                ref={calendarRef}
                plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                headerToolbar={{ left: 'prev,next today', center: 'title', right: 'timeGridWeek,dayGridMonth' }}
                height="auto"
                selectable
                editable
                droppable
                events={events}
                datesSet={handleDatesSet}
                select={handleSelect}
                eventClick={handleEventClick}
                eventDrop={handleEventDrop}
                eventResize={handleEventResize}
                drop={handleExternalDrop}
              />
            </div>
          </Card>
        </div>
      )}

      {createForm && (
        <CreateBlockModal
          form={createForm}
          assets={assets}
          playlists={playlists}
          onClose={() => setCreateForm(null)}
          onCreate={async (payload) => {
            setBusy(true);
            try {
              await PlayoutService.createSchedule({ ...payload, stream_id: streamId });
              await loadSchedules();
              setCreateForm(null);
            } catch (e) {
              window.alert(e.response?.data?.error || 'Failed to create this program');
            } finally { setBusy(false); }
          }}
          busy={busy}
        />
      )}

      {panelEvent && (
        <Overlay onClose={closeSidePanel}>
          <div className="tw-flex tw-items-start tw-justify-between">
            <div>
              <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-faint">
                {panelEvent.asset_id ? 'Single program' : 'Rotation'}
              </div>
              <div className="tw-mt-1 tw-text-lg tw-font-bold tw-text-heading">
                {panelEvent.asset_id ? (panelEvent.asset_title || 'Program') : (panelEvent.playlist_name || 'Rotation')}
              </div>
            </div>
            <Badge tone={panelEvent.asset_id ? 'brand' : 'blue'}>{panelEvent.asset_id ? 'Program' : 'Playlist'}</Badge>
          </div>

          <div className="tw-mt-4 tw-space-y-3 tw-text-sm">
            <div className="tw-flex tw-items-center tw-justify-between">
              <span className="tw-text-content-muted">Failover enabled</span>
              <Toggle checked={!!panelEvent.failover_enabled} onChange={toggleBlockFailover} />
            </div>
            {panelEvent.playlist_id && (
              <div className="tw-flex tw-items-center tw-justify-between">
                <span className="tw-text-content-muted">Auto Loop</span>
                <Toggle checked={!!panelEvent.loop_enabled} onChange={toggleBlockLoop} />
              </div>
            )}
          </div>

          <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-2">
            <Button variant="ghost" onClick={closeSidePanel}>Close</Button>
            <Button variant="danger" icon="fa-trash" onClick={deleteEvent} disabled={busy}>Remove</Button>
          </div>
        </Overlay>
      )}

      {fillGapsOpen && (
        <FillGapsModal
          streamId={streamId}
          range={range}
          assets={assets}
          playlists={playlists}
          onClose={() => setFillGapsOpen(false)}
          onDone={async () => { await loadSchedules(); setFillGapsOpen(false); }}
        />
      )}
    </div>
  );
};

const CreateBlockModal = ({ form, assets, playlists, onClose, onCreate, busy }) => {
  const [mode, setMode] = useState('asset');
  const [assetId, setAssetId] = useState(assets[0]?.id || '');
  const [playlistId, setPlaylistId] = useState(playlists[0]?.id || '');
  const [repeat, setRepeat] = useState(false);
  const [days, setDays] = useState([form.start.getDay()]);

  const submit = () => {
    const base = repeat
      ? { start_time: `${pad(form.start.getHours())}:${pad(form.start.getMinutes())}`, days_of_week: days }
      : { start_time: toLocalNaive(form.start), days_of_week: [0, 1, 2, 3, 4, 5, 6] };
    const payload = mode === 'asset'
      ? { ...base, asset_id: assetId, is_active: true, failover_enabled: true }
      : {
          ...base,
          playlist_id: playlistId, is_active: true, failover_enabled: true,
          end_time: repeat ? `${pad(form.end.getHours())}:${pad(form.end.getMinutes())}` : toLocalNaive(form.end),
        };
    onCreate(payload);
  };

  return (
    <Overlay onClose={onClose}>
      <div className="tw-text-lg tw-font-bold tw-text-heading">Schedule a program</div>
      <div className="tw-mt-1 tw-text-xs tw-text-content-faint">
        {form.start.toLocaleString()} {mode === 'playlist' && `→ ${form.end.toLocaleTimeString()}`}
      </div>

      <div className="tw-mt-4 tw-flex tw-gap-2">
        <Button size="sm" variant={mode === 'asset' ? 'primary' : 'secondary'} onClick={() => setMode('asset')}>Single program</Button>
        <Button size="sm" variant={mode === 'playlist' ? 'primary' : 'secondary'} onClick={() => setMode('playlist')}>Rotation</Button>
      </div>

      <div className="tw-mt-4 tw-space-y-3">
        {mode === 'asset' ? (
          <Field label="Program">
            <Select value={assetId} onChange={(e) => setAssetId(e.target.value)}>
              {assets.map((a) => <option key={a.id} value={a.id}>{a.title}{a.duration ? ` (${Math.round(a.duration / 60)} min)` : ''}</option>)}
            </Select>
          </Field>
        ) : (
          <Field label="Rotation / playlist">
            <Select value={playlistId} onChange={(e) => setPlaylistId(e.target.value)}>
              {playlists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        )}

        <div className="tw-flex tw-items-center tw-justify-between">
          <span className="tw-text-sm tw-text-content-muted">Repeat weekly</span>
          <Toggle checked={repeat} onChange={setRepeat} />
        </div>
        {repeat && (
          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {DOW.map((label, i) => (
              <button
                key={i} type="button"
                onClick={() => setDays((d) => (d.includes(i) ? d.filter((x) => x !== i) : [...d, i]))}
                className={`tw-rounded-md tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold ${days.includes(i) ? 'tw-bg-brand tw-text-white' : 'tw-bg-ink-800 tw-text-content-muted'}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-2">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy || (mode === 'asset' && !assetId) || (mode === 'playlist' && !playlistId)}>
          {busy ? 'Scheduling…' : 'Schedule it'}
        </Button>
      </div>
    </Overlay>
  );
};

const FillGapsModal = ({ streamId, range, assets, playlists, onClose, onDone }) => {
  const [strategy, setStrategy] = useState('filler');
  const [fillerAssetId, setFillerAssetId] = useState(assets[0]?.id || '');
  const [fillerPlaylistId, setFillerPlaylistId] = useState('');
  const [fillerType, setFillerType] = useState('asset');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setBusy(true); setError(null);
    try {
      const result = await PlayoutService.fillGaps({
        stream_id: streamId,
        range_start: toLocalNaive(range.start),
        range_end: toLocalNaive(range.end),
        strategy,
        filler_asset_id: strategy === 'filler' && fillerType === 'asset' ? fillerAssetId : undefined,
        filler_playlist_id: strategy === 'filler' && fillerType === 'playlist' ? fillerPlaylistId : undefined,
      });
      if (!result.gaps?.length) setError('No gaps found in the visible range — the schedule is already full.');
      else onDone();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fill gaps');
    } finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="tw-text-lg tw-font-bold tw-text-heading">Fill the gaps</div>
      <div className="tw-mt-1 tw-text-xs tw-text-content-faint">
        Finds empty time in the range currently visible on the calendar and fills it in.
      </div>

      <div className="tw-mt-4 tw-space-y-2">
        {[
          ['filler', 'Use a specific program or rotation'],
          ['rotate', 'Rotate everything in your library'],
          ['extend', 'Extend the program right before each gap'],
        ].map(([val, label]) => (
          <label key={val} className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-[10px] tw-bg-ink-800 tw-px-3 tw-py-2.5 tw-text-sm">
            <input type="radio" name="strategy" checked={strategy === val} onChange={() => setStrategy(val)} />
            {label}
          </label>
        ))}
      </div>

      {strategy === 'filler' && (
        <div className="tw-mt-3 tw-space-y-2">
          <div className="tw-flex tw-gap-2">
            <Button size="sm" variant={fillerType === 'asset' ? 'primary' : 'secondary'} onClick={() => setFillerType('asset')}>Program</Button>
            <Button size="sm" variant={fillerType === 'playlist' ? 'primary' : 'secondary'} onClick={() => setFillerType('playlist')}>Rotation</Button>
          </div>
          {fillerType === 'asset' ? (
            <Select value={fillerAssetId} onChange={(e) => setFillerAssetId(e.target.value)}>
              {assets.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
            </Select>
          ) : (
            <Select value={fillerPlaylistId} onChange={(e) => setFillerPlaylistId(e.target.value)}>
              <option value="">Select a playlist…</option>
              {playlists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          )}
        </div>
      )}

      {error && <div className="tw-mt-3 tw-rounded-[10px] tw-bg-live/10 tw-px-3 tw-py-2 tw-text-xs tw-text-live">{error}</div>}

      <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-2">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={run} disabled={busy}>{busy ? 'Filling…' : 'Fill gaps'}</Button>
      </div>
    </Overlay>
  );
};

export default Playout;
